// What is drawn *at* a square rather than in a corner.
//
// The wrapper takes nothing per-frame: every one of these anchors itself off
// `BoardRenderer.onFrame` and writes its own `style`, which is the rule that
// keeps a camera move from re-rendering the shell (PLAN-UI §28.1).
//
// Reads `SessionView` — `overlays` for the markers, `side` for whose pieces
// are whose — and `game.board.facts()` for the board
// itself (PLAN-CLIENT-SPLIT day 8). Every click goes back as an option.

import { useMemo, useSyncExternalStore } from "react";

import type { BoardFacts, Game } from "src/api";
import { BoardLabels } from "../BoardLabels";
import { CoFundMarks } from "../CoFundMarks";
import { HintOverlay } from "../HintOverlay";
import { LastMoveMarker } from "../LastMoveMarker";
import { PieceOverlays } from "../PieceOverlays";
import type { useHints } from "../useHints";
import type { BoardInteraction } from "../useBoardInteraction";
import { useBoardHandle } from "../shell/BoardHandle";
import { useGame } from "../useGame";
import { useSessionView } from "../useSessionView";

/**
 * The board on screen, as facts. Memoised by the engine on the shown board, so
 * `getSnapshot` is a lookup and a change of identity *is* a change of board.
 */
function useBoardFacts(game: Game): BoardFacts {
  return useSyncExternalStore(
    (listener) => game.on("turn", listener),
    () => game.board.facts(),
    () => game.board.facts()
  );
}

export function BoardOverlays({
  overBoard,
  hint
}: {
  /** @deprecated Unread: everything here is on `SessionView`. */
  board_?: BoardInteraction;
  /** Pointer over the board: what brings the file and rank labels up. */
  overBoard: boolean;
  /** One thing to try, from §16's curriculum. */
  hint: ReturnType<typeof useHints>;
}) {
  const board = useBoardHandle();
  const game = useGame();
  const view = useSessionView();
  const facts = useBoardFacts(game);
  // `CoFundMarks` still takes the model's map; the view says the same thing
  // as a list. Memoised on the list, which the engine rebuilds only when the
  // armed tile moves.
  const shared = useMemo(
    () => new Map(view.overlays.marks.map((mark) => [mark.square as number, mark.funders])),
    [view.overlays.marks]
  );

  return (
    <>
      <BoardLabels facts={facts} board={board} shown={overBoard} />

      {/* Markers only, and they cannot be clicked — every number and every
          button they used to carry is on the inspector now. They sat on top of
          the squares they described and took the clicks meant for them, which
          made reading about a piece and playing near it the same gesture. */}
      <PieceOverlays overlays={view.overlays} board={board} localSide={view.side} />

      {/* "×2" where a forge is being split between foundries (§28.4). Beside
          the markers because it is the same kind of thing — a fact drawn at
          the square it is about — and it lives only while a tile is armed. */}
      <CoFundMarks board={board} shared={shared} />

      {/* One thing to try, at the thing it is about. It is over the board
          rather than in the chrome because a hint has a *subject* — naming a
          pawn from a panel leaves the player to find the pawn, which is the
          hard half. It dims nothing and takes no click but its own, so the
          board underneath stays readable and stays playable. */}
      <HintOverlay hint={hint} board={board} />

      {/* Where the last move happened, when it happened off screen. The one
          other clickable thing over the board, and it earns it the same way
          the gizmo does: it exists only while there is somewhere to go, it
          sits at the edge rather than over the squares, and it is the *only*
          way to reach a camera action that is otherwise a key. It is not
          clipped — it is placed in the free area already, and a clip would cut
          the marker on the very edge it is put on. */}
      <LastMoveMarker />
    </>
  );
}
